// "Your avatar" on the Settings page: a person picks their face's colour, shape and accessory.
// Saves on every change, optimistically; a failure rolls back and says so.
// Doc: docs/features/user-avatars.md.

import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { Avatar, AvatarStack, isAvatarColor } from '../components/ui/Avatar';
import { ACCESSORY_LABELS, AVATAR_ACCESSORIES, AVATAR_SHAPES, SHAPES, isAvatarAccessory, isAvatarShape } from '../components/ui/avatarShapes';
import type { AvatarAccessory, AvatarShape } from '../components/ui/avatarShapes';
import { Button } from '../components/ui/Button';
import Panel from '../components/ui/Panel';
import { Tooltip } from '../components/ui/Tooltip';
import { RadioGroup, RadioSwatch, RadioTile } from '../components/ui/Radio';
import { CONTROL_MOTION } from '../components/ui/motion';

/** Show colours: what the drones fly, so a face can match a person's favourite cue. */
const SWATCHES: { hex: string; name: string }[] = [
  { hex: '#ffcc33', name: 'Firefly gold' },
  { hex: '#ff8a1f', name: 'Amber' },
  { hex: '#ff3b4e', name: 'Signal red' },
  { hex: '#ff4fb8', name: 'Magenta' },
  { hex: '#9b6bff', name: 'Violet' },
  { hex: '#3d8bff', name: 'Blue' },
  { hex: '#2fd6f0', name: 'Cyan' },
  { hex: '#38d996', name: 'Green' },
  { hex: '#b4ec3c', name: 'Lime' },
  { hex: '#f4f4f4', name: 'White' },
];

interface Style { avatar_color: string | null; avatar_shape: AvatarShape | null; avatar_accessory: AvatarAccessory | null }

interface Props { initialColor?: string | null; initialShape?: string | null; initialAccessory?: string | null }

const AvatarSettings: React.FC<Props> = ({ initialColor, initialShape, initialAccessory }) => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s: any) => s.user);
  const name = [user.firstname, user.lastname].filter(Boolean).join(' ') || 'You';

  const start: Style = {
    avatar_color: isAvatarColor(initialColor) ? initialColor.toLowerCase() : null,
    avatar_shape: isAvatarShape(initialShape) ? initialShape : null,
    avatar_accessory: isAvatarAccessory(initialAccessory) ? initialAccessory : null,
  };
  const [style, setStyle] = useState<Style>(start);
  const saved = useRef<Style>(start);
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<Style | null>(null);
  const [landed, setLanded] = useState(false);

  // The store carries the signed-in person's own face, so every Avatar with their
  // userId follows a change here without a refetch.
  const publish = (s: Style) => useUserStore.setState((st: any) => ({ user: { ...st.user, ...s } }));
  useEffect(() => {
    publish(start);
    // Leaving mid-drag still saves the colour the person stopped on.
    return () => {
      window.clearTimeout(timer.current);
      if (pending.current) save(pending.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (next: Style) => {
    pending.current = null;
    try {
      const res = await api.patch('/users/me/avatar', { body: next, silent: true });
      saved.current = { avatar_color: res?.avatar_color ?? null, avatar_shape: res?.avatar_shape ?? null, avatar_accessory: res?.avatar_accessory ?? null };
    } catch {
      setStyle(saved.current);
      publish(saved.current);
      toast.error('Could not save your avatar. Try again.');
    }
  };

  /** Apply now, save now (or once a dragged colour settles). */
  const change = (patch: Partial<Style>, settle = 0) => {
    const next = { ...style, ...patch };
    setStyle(next);
    publish(next);
    setLanded(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setLanded(false)));
    window.clearTimeout(timer.current);
    if (settle) {
      pending.current = next;
      timer.current = window.setTimeout(() => save(next), settle);
    } else save(next);
  };

  const shape = style.avatar_shape || 'circle';
  const isDefault = !style.avatar_color && !style.avatar_shape && !style.avatar_accessory;
  const face = { name, color: style.avatar_color, shape, accessory: style.avatar_accessory };

  return (
    <Panel
      title="Your avatar"
      className="mb-4"
      actions={
        <Tooltip label="Go back to the plain circle" note={isDefault ? 'You are on the default' : undefined}>
          <span>
            <Button variant="quiet" size="sm" disabled={isDefault} onClick={() => change({ avatar_color: null, avatar_shape: null, avatar_accessory: null })}>
              Use the default
            </Button>
          </span>
        </Tooltip>
      }
    >
      <p className="m-0 mb-4 text-sm text-on-surface-variant">Your initials, in your colour and shape, with something to wear if you like, wherever the team sees you.</p>

      <div className="grid gap-4 sm:grid-cols-[9rem_minmax(0,1fr)]">
        <div
          aria-label="Preview"
          role="group"
          className="flex items-center justify-center gap-5 rounded-control bg-surface-container-high px-3 pb-5 pt-8 shadow-recess sm:flex-col sm:gap-6"
        >
          <span className={`inline-flex transition-transform duration-spring ease-spring-bouncy ${landed ? 'motion-safe:scale-90' : 'scale-100'}`}>
            <Avatar {...face} size="md" ring="ring-surface-container-high" />
          </span>
          <Avatar {...face} size="sm" ring="ring-surface-container-high" decorative />
          <AvatarStack
            size="sm"
            ring="ring-surface-container-high"
            label="You beside two teammates"
            people={[{ key: 'me', ...face }, { key: 'a', name: 'Ada Lovelace' }, { key: 'b', name: 'Grace Hopper' }]}
          />
        </div>

        <div className="min-w-0 space-y-4">
          <RadioGroup
            label="Colour"
            name="avatar-color"
            layout="swatches"
            value={style.avatar_color}
            onChange={(hex) => change({ avatar_color: hex })}
          >
            {SWATCHES.map((s) => (
              <RadioSwatch key={s.hex} value={s.hex} color={s.hex} label={s.name} />
            ))}
            <label className="relative ml-1 inline-flex cursor-pointer items-center gap-2 text-sm text-on-surface-variant">
              <input
                type="color"
                value={style.avatar_color || '#3a3a3a'}
                onChange={(e) => change({ avatar_color: e.target.value.toLowerCase() }, 400)}
                className={`m-0 h-7 w-10 cursor-pointer rounded-control border-0 bg-surface-container-highest p-0.5 shadow-key ${CONTROL_MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50`}
              />
              Any colour
            </label>
          </RadioGroup>

          <RadioGroup
            label="Shape"
            name="avatar-shape"
            layout="track"
            columns={5}
            value={shape}
            onChange={(key) => change({ avatar_shape: key === 'circle' ? null : (key as AvatarShape) })}
          >
            {AVATAR_SHAPES.map((key) => (
              <RadioTile key={key} value={key} caption={SHAPES[key].label} className="pt-2">
                <Avatar name={name} color={style.avatar_color} shape={key} size="md" ring="ring-transparent" decorative />
              </RadioTile>
            ))}
          </RadioGroup>

          <RadioGroup
            label="Accessory"
            name="avatar-accessory"
            layout="track"
            columns={5}
            value={style.avatar_accessory || 'none'}
            onChange={(key) => change({ avatar_accessory: key === 'none' ? null : (key as AvatarAccessory) })}
          >
            {[null, ...AVATAR_ACCESSORIES].map((key) => (
              // A hat or a halo rises above the face, so these keys leave headroom.
              <RadioTile key={key || 'none'} value={key || 'none'} caption={key ? ACCESSORY_LABELS[key] : 'None'} className="pt-5">
                <Avatar name={name} color={style.avatar_color} shape={shape} accessory={key} size="md" ring="ring-surface-container-high" decorative />
              </RadioTile>
            ))}
          </RadioGroup>
        </div>
      </div>
    </Panel>
  );
};

export default AvatarSettings;
